/* eslint-disable react/display-name */
import { useContext, useEffect, useMemo } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';

// Context imports
import AuthContext from 'src/store/auth-context';
import AdminAuthContext from 'src/store/admin-auth-context';
import ToastContext from 'src/store/toast-context';
import { LoadedProvider } from 'src/store/load-context';

// Page and component imports
import LoginPage from 'src/pages/login';
import CompletePage from 'src/pages/complete';
import Page from 'src/components/page/Page';
import PersonalPropertyForm from 'src/components/PersonalPropertyForm';
import Steps from 'src/components/steps/steps';
import AdminHome from 'src/pages/adminHome';
import BackdoorAccountDetails from 'src/pages/adminAccountDetails';
import Error404Page from 'src/pages/404';
import AdminLoginPage from 'src/pages/adminLogin';

// Utility functions and hooks
import { uniqueId } from 'lodash';
import useAdminAPI from 'src/services/useAdminAPI';
import { useAPI } from 'src/services/api';
import { StepperProvider } from 'src/store/stepper-context';
import { AppContext } from 'src/store/app-context';

const makePage = (title, Component, isAdmin) => (props) => {
  const sessionURL = isAdmin ? '/admin/sessionToken' : '/account/sessionToken';
  const sessionContext = isAdmin ? AdminAuthContext : AuthContext;
  const sessionAPI = isAdmin ? useAdminAPI() : useAPI();

  return (
    <LoadedProvider>
      <StepperProvider>
        <Page
          title={title}
          sessionContext={sessionContext}
          refreshSessionURL={sessionURL}
          refreshSessionAPI={sessionAPI}
        >
          <Component {...props} />
        </Page>
      </StepperProvider>
    </LoadedProvider>
  );
};

const makeRedirect = (location) => () => {
  return <Redirect to={location} />;
};

// Usage is the same as the regular Route component, though also adds a condition that
// indicates if access to the route is permitted or not. If the condition is a function, it will be executed
// and it's return value will be used. Otherwise, it will be coerced to a boolean and evaluated. Also adds
// the deniedComponent and deniedRender props that are triggered when the page is restricted.
// deniedComponent and deniedRender props function in a manner similar to component and
// render props in the standard Route component.
const RestrictedRoute = ({
  component: Component,
  deniedComponent: DeniedComponent,
  render,
  deniedRender,
  condition,
  ...props
}) => {
  const routeAllowed = typeof condition === 'function' ? condition() : !!condition;
  return (
    <Route
      {...props}
      render={(routeProps) => {
        if (routeAllowed) {
          return Component ? <Component {...routeProps} /> : render(routeProps);
        } else {
          return DeniedComponent ? <DeniedComponent {...routeProps} /> : deniedRender(routeProps);
        }
      }}
    />
  );
};

const LoginRequiredRoute = ({ isAdmin, ...props }) => {
  const auth = useContext(isAdmin ? AdminAuthContext : AuthContext);
  const { autoLogoutPath } = useContext(AppContext);
  const location = useLocation();
  const { error } = useContext(ToastContext);
  const condition = props.condition ?? true;

  // Extracting and preparing query parameters for appending
  const queryParams = new URLSearchParams(location.search);
  const queryString = queryParams.toString(); // Converts all query parameters to a string

  useEffect(() => {
    if (!auth.logoutNotified && !auth.isLoggedIn) {
      error('Please log in.', {
        toastId: 'pageAccessToast',
      });
    }
  });

  const redirectPath = autoLogoutPath
    ? `${autoLogoutPath}?redirect=`
    : isAdmin
    ? `/admin/login?redirect=`
    : `/login?redirect=`;

  const encodeRedirect = encodeURIComponent(
    `${location.pathname}?${queryString ? `${queryString}` : ''}`
  );

  return (
    <RestrictedRoute
      {...props}
      condition={condition && auth.isLoggedIn}
      deniedComponent={makeRedirect(`${redirectPath}${encodeRedirect}`)}
    />
  );
};

function App() {
  const auth = useContext(AuthContext);

  const memoizedStepRoutes = useMemo(() => {
    return Steps.map(({ path, Component }, index) => {
      return (
        <LoginRequiredRoute
          key={uniqueId()}
          exact
          path={path}
          component={makePage('Personal Property', () => {
            return (
              <PersonalPropertyForm>
                <Component isFirstStep={index === 0} isLastStep={index === Steps.length - 1} />
              </PersonalPropertyForm>
            );
          })}
        />
      );
    });
  }, []);

  return (
    <div className="App">
      <Switch>
        <Route exact path={['/', '/login']} component={makePage('Login', LoginPage)} />

        <LoginRequiredRoute
          path="/complete"
          condition={auth.account?.signed === 'Y'}
          component={makePage('Complete', CompletePage)}
        />
        {memoizedStepRoutes}

        <Route
          exact
          path={['/admin', '/admin/login']}
          component={makePage('Login', AdminLoginPage, true)}
        />

        <LoginRequiredRoute
          exact
          path="/admin/home"
          component={makePage('Admin', AdminHome, true)}
          isAdmin={true}
        />
        <LoginRequiredRoute
          exact
          path="/admin/account/details"
          component={makePage('BackdoorAccountDetails', BackdoorAccountDetails, true)}
          isAdmin={true}
        />

        <Route path="*" component={makePage('Page Not Found', Error404Page)} />
      </Switch>
    </div>
  );
}

export default App;
